/*
 * Copyright (c) 2020. American Express Company
 */

// theirs
import { ChronoUnit, LocalDate } from '@js-joda/core';
import * as _ from 'lodash';
import React, { Component, ReactComponentElement } from 'react';

// models and services
import { CardModel } from 'src/types/card.model';
import FinanceService, { SocialSecurityResponse } from '../../services/finance.service';
import FormatService from '../../services/format.service';
import LiteralsService from '../../services/literals/LiteralsService';

// components
import { Label } from '@americanexpress/dls-react';
import Anchor from '../atomic/atoms/anchor/Anchor';
import BWCurrencyInput from 'src/bw/bw-currency-input/BW-Currency-Input';
import GoalDrawer from './GoalDrawer';
import InfoCallout from '../callout/InfoCallout';
import Output from '../atomic/molecules/output/Output';

// icons and styles
import { HELP_DOMAIN } from 'src/utils/constants';
import { ReactComponent as SocialSecurityIcon } from 'src/bw/goal/social-security.svg';

export default class CollectSocialSecurityDrawer extends Component<
    any,
    {
        percentage: number;
        estimated: number;
    }
> {
    public override state = {
        percentage: 100 as number,
        estimated: this.props.drawerProps.card.attributes.estimate_social
    };

    private lookupTimer!: number;

    public override componentDidMount(): void {
        this.startLookupTimer();
    }

    public override componentDidUpdate(prevProps: any, prevState: any, snapshot: any): void {
        const oldDate: LocalDate = _.get(prevProps, 'drawerProps.card.startDate');
        const newDate: LocalDate = _.get(this.props, 'drawerProps.card.startDate');
        if (oldDate !== newDate) {
            this.startLookupTimer();
        }
    }

    private getSocialSecurityData(): void {
        const card: CardModel = _.get(this, 'props.drawerProps.card');
        if (card && this.props.user.dob) {
            void FinanceService.retrieveSocialSecurity(card.startDate, card.attributes.average_income).then(
                (response: SocialSecurityResponse) => {
                    if (!response) return;

                    this.setState({
                        percentage: response.percentage,
                        estimated: response.estimated
                    });
                }
            );
        }
    }

    private startLookupTimer(): void {
        if (this.lookupTimer) {
            clearTimeout(this.lookupTimer);
        }
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        this.lookupTimer = window.setTimeout(this.getSocialSecurityData.bind(this), 1000);
    }

    public override render(): ReactComponentElement<any> {
        const drawerProps: any = this.props.drawerProps;

        const card: CardModel = drawerProps.card;
        let total = (this.state.estimated * this.state.percentage) / 100;

        let age: number = 67;
        if (this.props.user.dob) {
            age = this.props.user.dob.until(card.startDate, ChronoUnit.YEARS);
            if (age < 62) {
                total = 0;
            }
        }

        let outputSubtext: string;
        if (age < 62) {
            outputSubtext = 'You are not eligible for Social Security before age 62';
        } else if (age < 67) {
            outputSubtext = 'Includes penalty for taking Social Security early';
        } else if (age > 67) {
            outputSubtext = 'Includes extra for taking Social Security late';
        }

        return (
            <GoalDrawer
                drawerProps={drawerProps}
                handleBack={this.props.handleBack}
                handleChangeCard={this.props.handleChangeCard}
                handleUpdateCard={this.props.handleUpdateCard}
                handleDeleteCard={this.props.handleDeleteCard}
                showLoader={this.props.showLoader}
                hideLoader={this.props.hideLoader}
                icon={<SocialSecurityIcon />}
                introContent={LiteralsService.getJsxLiteral('CollectSocialSecurityDrawer.info')}
                startDateLabel={LiteralsService.getLiteral('CollectSocialSecurityDrawer.startDate')}
                cashFlow={this.props.user.cashFlow}
                handleChangeCardDate={this.handleChangeCardDate.bind(this)}
                learnMoreContent={LiteralsService.getJsxLiteral('CollectSocialSecurityDrawer.help')}
                learnMoreUrl={HELP_DOMAIN ? `${HELP_DOMAIN}/articles/step-6-preparing-to-retire/` : null}
                afterUpdateAction={this.afterUpdateAction.bind(this)}
                key="collect-social-security-drawer"
            >
                <div className="drawerSection">
                    <div>
                        <Label htmlFor="average_income">Average yearly income</Label>
                        <BWCurrencyInput
                            fullWidth={true}
                            id="average_income"
                            defaultValue={0}
                            status=""
                            value={Number(card.attributes.average_income).toFixed(0)}
                            showFractions={false}
                            onChange={(value: number) => {
                                this.handleChangeAnnual(value);
                            }}
                        />
                    </div>
                    <InfoCallout noMargin>
                        We use your average yearly income to estimate your Social Security monthly benefits. To get your
                        lifetime income for a more accurate estimate, visit the{' '}
                        <Anchor target="_blank" rel="noopener noreferrer" href="https://www.ssa.gov/">
                            US Social Security Administration site
                        </Anchor>
                        .
                    </InfoCallout>
                </div>

                <Output
                    title={LiteralsService.getLiteral('CollectSocialSecurityDrawer.monthly')}
                    main={FormatService.formatString(total)}
                    subtext={outputSubtext}
                />
            </GoalDrawer>
        );
    }

    private handleChangeAnnual(value: any): void {
        const annual: number = Number(String(value).replace(/[$,]/g, ''));
        const monthly: number = FinanceService.estimatedSocialSecurity(annual);

        const updatedCard: CardModel = { ...this.props.drawerProps.card };
        updatedCard.attributes = {
            ...updatedCard.attributes
        };

        updatedCard.attributes.average_income = annual;
        updatedCard.attributes.estimate_social = monthly;
        updatedCard.attributes.newAnnualIncome = monthly * 12;

        this.props.updateDrawer({
            ...this.props.drawerProps,
            card: updatedCard
        });

        this.startLookupTimer();
    }

    private async afterUpdateAction(): Promise<void> {
        this.props.handleBack();
    }

    private async handleChangeCardDate(date: LocalDate, name: string): Promise<void> {
        this.startLookupTimer();
        await this.props.handleChangeCardDate(date, name);
    }
}
